import clsx from 'clsx';
import { Avatar, Icon, InputCheckbox } from 'ui';
import InputPoints from 'core/modules/atoms/InputPoints';
import InputPointsNumericScale from 'core/modules/atoms/InputPointsNumericScale';
import InputPointsQualitativeScale from 'core/modules/atoms/InputPointsQualitativeScale/InputPointsQualitativeScale';
import Score from 'core/modules/atoms/Score';
import { isScaleNumeric, isScaleQualitative } from 'core/utils/constants/evaluation';
import { cleanEmail, getStudentDisplayName } from 'core/utils/user';
import { roundNumber } from 'core/utils/utils';
import { debounce } from 'lodash';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import TooltipCustom from 'ui/atoms/Tooltip/Tooltip';

const UserItem = ({
  guid,
  name,
  lastName,
  lastname,
  email,
  thumbnail,
  score,
  checkboxsRight,
  showPoints,
  showCheckbox,
  checked,
  isScore,
  isScoreReadOnly,
  isBooleanScore,
  isLate,
  iconAction,
  provisionalScore,
  onClick,
  onChange,
  onAction,
  activeUser,
  onScoreChange,
  shouldSelectOnInput,
  sizeAvatar,
  customRight,
  warningLeft,
  scoreScale,
  colorsPoints,
  disabledInputPoints = false,
  item,
  evaluationScale,
  waitReturn = false,
  showLastNameFirst,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [changeErrorPoints, setChangeErrorPoints] = useState(false);
  const { t } = useTranslation();

  const displayName = getStudentDisplayName(name, lastName, showLastNameFirst, lastname);

  const handleScoreChange = useCallback(
    async (data) => {
      try {
        await onScoreChange(data);
      } finally {
        setIsLoading(false);
      }
    },
    [onScoreChange]
  );

  const [debouncedUpdateScore] = useState(() => debounce(handleScoreChange, 1000));

  useEffect(() => {
    return () => {
      debouncedUpdateScore.cancel();
      setIsLoading(false);
    };
  }, [debouncedUpdateScore]);

  const handleInputChange = useCallback(
    (params) => {
      setIsLoading(true);
      debouncedUpdateScore({ ...item, score: params });
    },
    [debouncedUpdateScore, item]
  );

  const handleQualitativeChange = useCallback(
    async (params) => {
      setIsLoading(true);
      await handleScoreChange({ ...item, score: params });
    },
    [handleScoreChange, item]
  );

  const showLoading = isLoading && waitReturn;

  return (
    <div
      id={'user-item-' + guid}
      className={clsx('user-item', {
        'user-item--checkboxs-right': checkboxsRight,
        selected: activeUser && activeUser.guid === guid,
        'user-item--checked': checked,
        'user-item--without-points': !showPoints,
      })}
    >
      <div className="user-item__container">
        <div className="user-item__main-info">
          {warningLeft && <div className="user-item__warning-left">{warningLeft}</div>}
          {showCheckbox && (
            <div className="user-item__checkbox-wrapper">
              <div className={clsx('user-item__input-checkbox', { 'user-item__input-checkbox--hidden': showLoading })}>
                <InputCheckbox
                  checked={checked}
                  disabled={showLoading}
                  // Hidden label gives the checkbox an accessible name. The visible name below is
                  // not reused as the label because clicking it opens the user, not toggles the checkbox.
                  label={<span className="sr-only">{displayName}</span>}
                  onChange={(e) => {
                    onChange(e, guid);
                    onClick(activeUser);
                  }}
                />
              </div>
              <div className={clsx('user-item__loading', { 'user-item__loading--visible': showLoading })}>
                <div className="user-item__loading-spinner" />
              </div>
            </div>
          )}
          <div
            className="user-item__avatar-name"
            {...(shouldSelectOnInput
              ? {
                  onClick: () => {
                    onClick(activeUser);
                  },
                }
              : {})}
          >
            <div className="user-item__avatar">
              <Avatar image={thumbnail} size={sizeAvatar} />
            </div>
            <div className="user-item__texts">
              <span className="user-item__name">{displayName}</span>
              <span className="user-item__email"> {cleanEmail(email)}</span>
            </div>
          </div>
        </div>

        {showPoints && (
          <div
            className="user-item__input-points"
            {...(shouldSelectOnInput
              ? {
                  onClick: () => {
                    onClick(activeUser);
                  },
                }
              : {})}
          >
            <>
              {isScaleNumeric(evaluationScale) ? (
                <InputPointsNumericScale
                  colorsPoints={colorsPoints}
                  disabled={disabledInputPoints}
                  showDotted={provisionalScore}
                  isLate={isLate}
                  min={evaluationScale?.min ? evaluationScale?.min : 0}
                  max={evaluationScale?.max}
                  evaluationScale={evaluationScale}
                  isScoreReadOnly={isScoreReadOnly}
                  value={score === null && score !== 0 ? '-' : score}
                  onChange={handleInputChange}
                  changeError={setChangeErrorPoints}
                  numberDecimals={evaluationScale?.number_decimals}
                />
              ) : isScaleQualitative(evaluationScale) ? (
                <InputPointsQualitativeScale
                  t={t}
                  colorsPoints={colorsPoints}
                  disabled={disabledInputPoints}
                  showDotted={provisionalScore}
                  isLate={isLate}
                  evaluationScale={evaluationScale}
                  isScoreReadOnly={isScoreReadOnly}
                  value={score === null && score !== 0 ? '-' : score}
                  onChange={handleQualitativeChange}
                />
              ) : (
                <InputPoints
                  scoreScale={scoreScale}
                  colorsPoints={colorsPoints}
                  disabled={disabledInputPoints}
                  showDotted={provisionalScore}
                  isLate={isLate}
                  isScoreReadOnly={isScoreReadOnly}
                  value={score === null && score !== 0 ? '-' : roundNumber(score, 2)}
                  onChange={handleInputChange}
                  max={scoreScale > 0 ? scoreScale : undefined}
                />
              )}
            </>
          </div>
        )}
        {isScore && <Score score={score} scoreScale={scoreScale} evaluationScale={evaluationScale} />}
        {onAction && (
          <div className="user-item__action" onClick={() => onAction(guid)}>
            <Icon name={iconAction} />
          </div>
        )}
        {isBooleanScore && score !== null && (
          <div className="user-item__boolean-score">
            <div className="user-item__boolean-score-icon">
              <Icon size="xl" name={score === 0 ? 'incorrect-circle' : 'done-circle'} />
            </div>
            {isLate && (
              <div className="input-points__late">
                <TooltipCustom title={t('lesson:Delivered late')} placement="top" hasArrow>
                  <div>
                    <Icon name="exclamation-circle" />
                  </div>
                </TooltipCustom>
              </div>
            )}
          </div>
        )}
        {customRight}
      </div>
      {changeErrorPoints && (
        <div className="input-points__error-message">
          {t('assigments:The score must be between min and max', { min: evaluationScale?.min, max: evaluationScale?.max })}
        </div>
      )}
    </div>
  );
};

UserItem.propTypes = {
  activeUser: PropTypes.object,
  waitReturn: PropTypes.bool,
  checkboxsRight: PropTypes.bool,
  checked: PropTypes.bool,
  colorsPoints: PropTypes.object,
  customRight: PropTypes.node,
  warningLeft: PropTypes.node,
  disabledInputPoints: PropTypes.bool,
  email: PropTypes.string,
  feedbackQuestionType: PropTypes.string,
  guid: PropTypes.string,
  iconAction: PropTypes.string,
  isBooleanScore: PropTypes.bool,
  isFeedbackActivity: PropTypes.bool,
  isLate: PropTypes.bool,
  isScore: PropTypes.bool,
  isScoreReadOnly: PropTypes.bool,
  item: PropTypes.object,
  lastName: PropTypes.string,
  lastname: PropTypes.string,
  name: PropTypes.string,
  note: PropTypes.number,
  onAction: PropTypes.func,
  onChange: PropTypes.func,
  onClick: PropTypes.func,
  onScoreChange: PropTypes.func,
  provisionalScore: PropTypes.bool,
  score: PropTypes.number,
  scoreScale: PropTypes.number,
  shouldSelectOnInput: PropTypes.bool,
  showCheckbox: PropTypes.bool,
  showLastNameFirst: PropTypes.bool,
  showPoints: PropTypes.bool,
  sizeAvatar: PropTypes.oneOf(['sm', 'md']),
  thumbnail: PropTypes.string,
  evaluationScale: PropTypes.object,
};

UserItem.defaultProps = {
  waitReturn: false,
  showPoints: true,
  showCheckbox: true,
  isScore: false,
  shouldSelectOnInput: false,
  provisionalScore: false,
  sizeAvatar: 'sm',
  feedbackQuestionType: '',
  item: {},
  isFeedbackActivity: false,
  onScoreChange: () => {},
};

export default UserItem;
